import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'

const FAVORITES_STORAGE_KEY = 'github_repos_favorites'

interface Favorite {
  id: number
  full_name: string
  addedAt: number
}

export interface FavoritesContextValue {
  favorites: number[]
  toggleFavorite: (repoId: number, fullName: string) => void
  isFavorite: (repoId: number) => boolean
  clearFavorites: () => void
  favoritesCount: number
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null)

function persistFavorites(map: Map<number, Favorite>) {
  try {
    localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify([...map.values()])
    )
  } catch (error) {
    console.error('Error saving favorites:', error)
  }
}

function useFavoritesState(): FavoritesContextValue {
  const [favoriteById, setFavoriteById] = useState<Map<number, Favorite>>(
    () => new Map()
  )

  useEffect(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY)
      if (stored) {
        const list: Favorite[] = JSON.parse(stored)
        setFavoriteById(new Map(list.map((f) => [f.id, f])))
      }
    } catch (error) {
      console.error('Error loading favorites:', error)
    }
  }, [])

  const toggleFavorite = useCallback((repoId: number, fullName: string) => {
    setFavoriteById((prev) => {
      const next = new Map(prev)
      if (next.has(repoId)) {
        next.delete(repoId)
      } else {
        next.set(repoId, {
          id: repoId,
          full_name: fullName,
          addedAt: Date.now(),
        })
      }
      persistFavorites(next)
      return next
    })
  }, [])

  const isFavorite = useCallback(
    (repoId: number) => favoriteById.has(repoId),
    [favoriteById]
  )

  const clearFavorites = useCallback(() => {
    setFavoriteById(new Map())
    try {
      localStorage.removeItem(FAVORITES_STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing favorites:', error)
    }
  }, [])

  return {
    favorites: [...favoriteById.keys()],
    toggleFavorite,
    isFavorite,
    clearFavorites,
    favoritesCount: favoriteById.size,
  }
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const value = useFavoritesState()
  return (
    <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
  )
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {
    throw new Error('useFavorites must be used within a <FavoritesProvider>')
  }
  return ctx
}
